import {
  CheckBoxOptionType,
  OptionType,
} from "@portal/BaseComponents/CheckBox/type";
import { MultiSelectElement } from "@portal/BaseComponents/Filters/Elements/MultiSelect/MultiSelectElement";
import { FilterDropdownDown } from "@portal/BaseComponents/Filters/FilterDropdown/FilterDropdownDown";
import { HideWrapper } from "@portal/BaseComponents/HideWrapper/HideWrapper";
import useOpen from "@portal/hooks/useOpen";
import { invoiceFiltersAtom } from "@portal/store/Filters/Invoices/invoice-filter";
import { resetPage, tableAtom } from "@portal/store/table/table";
import {
  InvoiceStatus,
  InvoiceStatusesConfig,
} from "@portal/types/entities/invoice";
import produce from "immer";
import { useAtom, useSetAtom } from "jotai";
import { useMemo } from "react";
import {
  getInvoiceStatusConfig,
} from "../../predicate";
import { getMultiSelectDisplayValue } from "@portal/BaseComponents/Filters/utils/multiSelectDisplayUtils";

export interface InvoiceStatusConfigProps {
  invoiceStatusConfig: InvoiceStatusesConfig;
}

const optionLabelPredicate = (option: InvoiceStatus) =>
  getInvoiceStatusConfig(option).label;
const optionsPredicate = (
  option: InvoiceStatus
): CheckBoxOptionType<InvoiceStatus> => {
  return {
    label: getInvoiceStatusConfig(option).label,
    value: option,
  };
};

function InvoiceStatusFilter(props: InvoiceStatusConfigProps) {
  const { onClose, open, toggleOpen, handleOpenChange } = useOpen({
    open: false,
  });
  const setTable = useSetAtom(tableAtom);
  const [invoiceFilters, setInvoiceFilters] = useAtom(invoiceFiltersAtom);

  function handleHideReset() {
    return invoiceFilters.filterValues.statuses?.length
      ? invoiceFilters.filterValues.statuses.length === options.length
      : true;
  }
  function handleReset() {
    setInvoiceFilters((filter) => {
      return produce(filter, (draft) => {
        draft.filterValues.statuses =
          props.invoiceStatusConfig[draft.ui_filters.view_status];

        return draft;
      });
    });
    setTable(resetPage);
    onClose();
  }

  function handleSubmit(value: OptionType["value"][]) {
    setInvoiceFilters((filter) => {
      return produce(filter, (draft) => {
        draft.filterValues.statuses = value as InvoiceStatus[];
        return draft;
      });
    });
    setTable(resetPage);
    onClose();
  }

  function getOptions() {
    return [
      ...props.invoiceStatusConfig[invoiceFilters.ui_filters.view_status],
    ].map(optionsPredicate);
  }

  const options = useMemo(getOptions, [
    invoiceFilters.ui_filters.view_status,
    props.invoiceStatusConfig,
  ]);

  function getSelectedFilterValues() {
    return getMultiSelectDisplayValue({
      selectedValues: invoiceFilters.filterValues.statuses ?? [],
      allOptions: options.map((opt) => opt.value),
      labelMapper: optionLabelPredicate,
    });
  }

  const selectedValues = useMemo(getSelectedFilterValues, [invoiceFilters.filterValues.statuses, options]);

  const hideReset = useMemo(handleHideReset, [
    invoiceFilters.filterValues.statuses,
    options.length,
  ]);

  const Content = (
    <MultiSelectElement<InvoiceStatus>
      multiSelectProps={{
        initialValue:
          invoiceFilters.filterValues.statuses ?? props.invoiceStatusConfig.ALL,
        options: options ?? [],
        onSubmit: handleSubmit,
        onCancel: onClose,
      }}
    />
  );

  return (
    <HideWrapper hide={!invoiceFilters.activeFilterKeys.includes("statuses")}>
      <FilterDropdownDown
        filterBlockProps={{
          isDefault: true,
          toggleOpen,
          open,
          title: "Invoice Status",
          filterValues: selectedValues,
          onReset: handleReset,
          hideReset,
        }}
        filterComponent={Content}
        filterTitle={"Invoice Status"}
        onopenChange={handleOpenChange}
      />
    </HideWrapper>
  );
}

export { InvoiceStatusFilter };
